import http from "@/api/request";
import { SYSTEM_SERVICE_BASE } from "@/config/apiBaseUrlConfig";

import type {
  AddAreaTagRequest,
  AreaTagRequest,
  AreaTagStatus,
  AreaTagTerritoryConflictResultVO,
  AreaTagTerritoryItemVO,
  AreaTagTerritorySaveRequest,
  AreaTagTreeNodeVO,
  AreaTagVO,
  BrandTagCreateRequest,
  BrandTagDetailVO,
  BrandTagListResultVO,
  BrandTagStatus,
  BrandTagUpdateRequest,
  GetAreaTagListParams,
  GetAreaTagTreeChildrenParams,
  GetBrandTagListParams,
} from "./type";

/** 品牌标签模块 API 前缀 */
const BRAND_TAG_BASE = `${SYSTEM_SERVICE_BASE}/brandTag`;

/** 懒加载区域标签树子节点 */
export const getAreaTagTreeChildren = (params: GetAreaTagTreeChildrenParams) => {
  return http.get<AreaTagTreeNodeVO[]>(`${SYSTEM_SERVICE_BASE}/areaTag/getAreaTagTreeChildren`, {
    params,
  });
};

/**
 * 查询顶层区域标签列表
 * @param params.keyword 关键词，模糊匹配区域名称或区域代码
 */
export const getAreaTagList = (params?: GetAreaTagListParams) => {
  return http.get<AreaTagVO[]>(`${SYSTEM_SERVICE_BASE}/areaTag/getAreaTagList`, { params });
};

/** 按 ID 查询区域标签详情 */
export const getAreaTagById = (id: string) => {
  return http.get<AreaTagVO>(`${SYSTEM_SERVICE_BASE}/areaTag/getAreaTagById/${id}`);
};

/** 查询区域已选地盘 */
export const getAreaTagTerritoriesByAreaTagId = (id: string) => {
  return http.get<AreaTagTerritoryItemVO[]>(
    `${SYSTEM_SERVICE_BASE}/areaTag/getAreaTagTerritoriesByAreaTagId/${id}`,
  );
};

/** 新增区域标签 */
export const addAreaTag = (data: AddAreaTagRequest) => {
  return http.post(`${SYSTEM_SERVICE_BASE}/areaTag/addAreaTag`, data);
};

/** 编辑区域标签 */
export const editAreaTag = (id: string, data: AreaTagRequest) => {
  return http.put(`${SYSTEM_SERVICE_BASE}/areaTag/editAreaTag/${id}`, data);
};

/**
 * 更新区域标签启停状态
 * @param status ENABLED 启用 / DISABLED 停用
 */
export const updateAreaTagStatus = (id: string, status: AreaTagStatus) => {
  return http.put(`${SYSTEM_SERVICE_BASE}/areaTag/updateAreaTagStatus/${id}`, undefined, {
    params: { status },
  });
};

/** 删除区域标签 */
export const deleteAreaTag = (id: string) => {
  return http.delete(`${SYSTEM_SERVICE_BASE}/areaTag/deleteAreaTag/${id}`);
};

/**
 * 全量保存区域标签地盘
 * PUT /service-chijing-system/api/system/areaTag/updateAreaTagTerritories/{id}
 * @param id 区域标签主键
 * @param data.items 勾选的地盘列表，空列表表示清空
 */
export const updateAreaTagTerritories = (id: string, data: AreaTagTerritorySaveRequest) => {
  return http.put<AreaTagTerritoryConflictResultVO>(
    `${SYSTEM_SERVICE_BASE}/areaTag/updateAreaTagTerritories/${id}`,
    data,
  );
};

/**
 * 查询品牌标签列表
 * GET /service-chijing-system/api/system/brandTag/getBrandTagList
 * @param params.keyword 关键词，模糊匹配品牌名称或所属行业
 */
export const getBrandTagList = (params?: GetBrandTagListParams) => {
  return http.get<BrandTagListResultVO>(`${BRAND_TAG_BASE}/getBrandTagList`, { params });
};

/**
 * 按 ID 查询品牌标签详情
 * GET /service-chijing-system/api/system/brandTag/getBrandTagById/{id}
 * @param id 品牌标签主键
 */
export const getBrandTagById = (id: string) => {
  return http.get<BrandTagDetailVO>(`${BRAND_TAG_BASE}/getBrandTagById/${id}`);
};

/**
 * 新增品牌标签
 * POST /service-chijing-system/api/system/brandTag/addBrandTag
 * @param data 新增品牌标签请求
 */
export const addBrandTag = (data: BrandTagCreateRequest) => {
  return http.post<BrandTagDetailVO>(`${BRAND_TAG_BASE}/addBrandTag`, data);
};

/**
 * 更新品牌标签
 * PUT /service-chijing-system/api/system/brandTag/updateBrandTag/{id}
 * @param id 品牌标签主键
 * @param data 更新品牌标签请求
 */
export const updateBrandTag = (id: string, data: BrandTagUpdateRequest) => {
  return http.put<BrandTagDetailVO>(`${BRAND_TAG_BASE}/updateBrandTag/${id}`, data);
};

/**
 * 更新品牌标签启停状态
 * PUT /service-chijing-system/api/system/brandTag/updateBrandTagStatus/{id}
 * @param id 品牌标签主键
 * @param status ENABLED 启用 / DISABLED 停用
 */
export const updateBrandTagStatus = (id: string, status: BrandTagStatus) => {
  return http.put(`${BRAND_TAG_BASE}/updateBrandTagStatus/${id}`, undefined, {
    params: { status },
  });
};

/**
 * 删除品牌标签
 * DELETE /service-chijing-system/api/system/brandTag/deleteBrandTag/{id}
 * @param id 品牌标签主键
 */
export const deleteBrandTag = (id: string) => {
  return http.delete(`${BRAND_TAG_BASE}/deleteBrandTag/${id}`);
};
